import React, { useState } from 'react';
import { Gift, User, Heart, Sparkles, Home, Camera, Sun, Moon } from 'lucide-react';

const ForHerPage = ({ setCurrentPage }) => {
  const [selectedStyle, setSelectedStyle] = useState('all');

  const femaleStyles = [
    { id: 'elegant', name: 'Elegant & Klassisk', icon: Sparkles, color: 'purple' },
    { id: 'cozy', name: 'Koselig & Hjemmekjær', icon: Home, color: 'pink' },
    { id: 'active', name: 'Aktiv & Eventyrlyst', icon: Sun, color: 'green' },
    { id: 'creative', name: 'Kreativ & Kunstnerisk', icon: Camera, color: 'blue' },
    { id: 'mindful', name: 'Mindful & Wellness', icon: Moon, color: 'indigo' },
    { id: 'social', name: 'Sosial & Utadvendt', icon: Heart, color: 'red' }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 via-rose-50 to-purple-50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">👩</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Gaver til henne</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Finn elegante og personlige gaver som får henne til å stråle. Fra skjønnhet og livsstil 
              til opplevelser og drømmer - vi hjelper deg å velge noe som virkelig berører henne.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-purple-600 text-white rounded-lg hover:from-primary-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn gaver til henne
            </button>
          </div>
        </div>
      </section>

      {/* Understanding Her */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Hva gjør en gave perfekt for henne?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-primary-50 to-rose-50 rounded-lg p-6 border border-primary-100">
                <Heart className="w-12 h-12 text-primary-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Emosjonell forbindelse</h3>
                <p className="text-secondary-600">
                  Kvinner setter ofte stor pris på gaver som viser at du kjenner hennes drømmer, 
                  ønsker og personlighet. Det handler om følelsen bak gaven, ikke bare objektet.
                </p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-100">
                <Sparkles className="w-12 h-12 text-purple-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Opplevelser og minner</h3>
                <p className="text-secondary-600">
                  Mange kvinner verdsetter opplevelser høyt - noe som skaper minner og kan deles 
                  med de hun elsker. Tenk utover fysiske gjenstander.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-cyan-50 rounded-lg p-6 border border-accent-100">
                <User className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Personlig utfoldelse</h3>
                <p className="text-secondary-600">
                  Gaver som støtter hennes interesser, kreativitet eller personlige utvikling 
                  viser at du ser og verdsetter hvem hun er og hvor hun ønsker å vokse.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Style Categories */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Utforsk gaver basert på hennes stil og personlighet
            </h2>

            {/* Style Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setSelectedStyle('all')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  selectedStyle === 'all'
                    ? 'bg-primary-500 text-white border-primary-500'
                    : 'bg-white text-secondary-700 border-secondary-300 hover:border-primary-300'
                }`}
              >
                Alle stiler
              </button>
              {femaleStyles.map((style) => (
                <button
                  key={style.id}
                  onClick={() => setSelectedStyle(style.id)}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${
                    selectedStyle === style.id
                      ? `bg-${style.color}-500 text-white border-${style.color}-500`
                      : 'bg-white text-secondary-700 border-secondary-300 hover:border-secondary-400'
                  }`}
                >
                  <style.icon className="w-4 h-4" />
                  <span>{style.name}</span>
                </button>
              ))}
            </div>

            {/* Style Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {femaleStyles.map((style) => (
                <div
                  key={style.id}
                  className={`bg-white rounded-lg shadow-sm p-6 border border-secondary-100 hover:shadow-md transition-all ${
                    selectedStyle === 'all' || selectedStyle === style.id ? 'block' : 'hidden'
                  }`}
                >
                  <style.icon className={`w-12 h-12 text-${style.color}-500 mb-4`} />
                  <h3 className="text-lg font-semibold text-secondary-800 mb-3">{style.name}</h3>
                  
                  {style.id === 'elegant' && (
                    <div className="space-y-3">
                      <p className="text-secondary-600 text-sm">For henne som elsker sofistikert luksus</p>
                      <ul className="space-y-1 text-sm text-secondary-600">
                        <li>• Tidløse smykker i sølv eller gull</li>
                        <li>• Designerparfyme og luksus hudpleie</li>
                        <li>• Silkeskjerf og cashmere-plagg</li>
                        <li>• Fine viner og gourmet-opplevelser</li>
                        <li>• Opera, teater og kulturelle opplevelser</li>
                      </ul>
                      <div className="bg-purple-50 p-3 rounded text-xs text-purple-700">
                        💎 Tips: Kvalitet over kvantitet - velg noen få, men ekstraordinære ting
                      </div>
                    </div>
                  )}

                  {style.id === 'cozy' && (
                    <div className="space-y-3">
                      <p className="text-secondary-600 text-sm">For henne som elsker hjemmekos og hygge</p>
                      <ul className="space-y-1 text-sm text-secondary-600">
                        <li>• Myke pledd og kashmere-tøfler</li>
                        <li>• Duftlys og aromaterapi</li>
                        <li>• Te-kolleksjoner og fine krus</li>
                        <li>• Bøker og lesekrok-tilbehør</li>
                        <li>• Hjemme-spa og badsalt</li>
                      </ul>
                      <div className="bg-primary-50 p-3 rounded text-xs text-primary-700">
                        🏠 Tips: Skape en atmosfære av ro og komfort
                      </div>
                    </div>
                  )}

                  {style.id === 'active' && (
                    <div className="space-y-3">
                      <p className="text-secondary-600 text-sm">For henne som elsker aktivitet og eventyr</p>
                      <ul className="space-y-1 text-sm text-secondary-600">
                        <li>• Treningstøy og sportsutstyr</li>
                        <li>• Utendørs aktiviteter og opplevelser</li>
                        <li>• Fitness-tracker og trenings-apps</li>
                        <li>• Reise-tilbehør og ryggsekker</li>
                        <li>• Klatring, surfing eller yoga-kurs</li>
                      </ul>
                      <div className="bg-success-50 p-3 rounded text-xs text-success-700">
                        🏃‍♀️ Tips: Støtt hennes aktive livsstil og nye utfordringer
                      </div>
                    </div>
                  )}

                  {style.id === 'creative' && (
                    <div className="space-y-3">
                      <p className="text-secondary-600 text-sm">For henne som uttrykker seg gjennom kreativitet</p>
                      <ul className="space-y-1 text-sm text-secondary-600">
                        <li>• Kunst- og håndverksmaterialer</li>
                        <li>• Kamera og fotografiutstyr</li>
                        <li>• Kreative workshops og kurs</li>
                        <li>• Vintage og unike samlergjenstander</li>
                        <li>• Design-bøker og inspirasjon</li>
                      </ul>
                      <div className="bg-accent-50 p-3 rounded text-xs text-accent-700">
                        🎨 Tips: Nær hennes kreative side og gi rom for utfoldelse
                      </div>
                    </div>
                  )}

                  {style.id === 'mindful' && (
                    <div className="space-y-3">
                      <p className="text-secondary-600 text-sm">For henne som prioriterer indre ro og balanse</p>
                      <ul className="space-y-1 text-sm text-secondary-600">
                        <li>• Meditasjon og mindfulness-redskaper</li>
                        <li>• Yoga-utstyr og retreats</li>
                        <li>• Naturlige hudpleie og økologisk skjønnhet</li>
                        <li>• Journaler og selvrefleksjon-bøker</li>
                        <li>• Krystaller og spirituelle objekter</li>
                      </ul>
                      <div className="bg-indigo-50 p-3 rounded text-xs text-indigo-700">
                        🧘‍♀️ Tips: Støtt hennes reise mot indre fred og velvære
                      </div>
                    </div>
                  )}

                  {style.id === 'social' && (
                    <div className="space-y-3">
                      <p className="text-secondary-600 text-sm">For henne som elsker å dele opplevelser</p>
                      <ul className="space-y-1 text-sm text-secondary-600">
                        <li>• Party- og underholdningsutstyr</li>
                        <li>• Opplevelser for vennegrupper</li>
                        <li>• Kokke- og vertinne-tilbehør</li>
                        <li>• Reise-opplevelser og festivaler</li>
                        <li>• Sosiale hobbyer og fellesskap</li>
                      </ul>
                      <div className="bg-error-50 p-3 rounded text-xs text-error-700">
                        💖 Tips: Gi opplevelser som kan deles med de hun elsker
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Life Stages */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Gaver tilpasset hennes livsfase
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-primary-50 to-rose-50 rounded-lg p-6 border border-primary-100">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">18-25 år</h3>
                <p className="text-secondary-600 text-sm mb-3">Utforskning og selvfunning</p>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Skjønnhetsprodukter og eksperimentering</li>
                  <li>• Reise-opplevelser og eventyr</li>
                  <li>• Utdanning og karrièreutvikling</li>
                  <li>• Sosiale aktiviteter og vennskap</li>
                  <li>• Mote og personlig stil</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-6 border border-purple-100">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">26-35 år</h3>
                <p className="text-secondary-600 text-sm mb-3">Etablering og forhold</p>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Hjemminnredning og nesting</li>
                  <li>• Profesjonell utvikling</li>
                  <li>• Kvalitet over kvantitet</li>
                  <li>• Romantiske opplevelser</li>
                  <li>• Helse og fitness-fokus</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-cyan-50 rounded-lg p-6 border border-accent-100">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">36-50 år</h3>
                <p className="text-secondary-600 text-sm mb-3">Familie og balanse</p>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Tid for seg selv og avslapning</li>
                  <li>• Familie-opplevelser</li>
                  <li>• Wellness og selvpleie</li>
                  <li>• Hobbyer og kreative utløp</li>
                  <li>• Luksus-oppgraderinger</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">50+ år</h3>
                <p className="text-secondary-600 text-sm mb-3">Visdom og nytelse</p>
                <ul className="text-sm text-secondary-600 space-y-1">
                  <li>• Kulturelle opplevelser</li>
                  <li>• Reiser og drømmedestinasjon</li>
                  <li>• Kvalitetstid med familie</li>
                  <li>• Arvesting og minner</li>
                  <li>• Spirituell utvikling</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Relationship Considerations */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Din relasjon bestemmer gavetypen
            </h2>

            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-error-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">💕 Partner/Kjæreste</h3>
                <p className="text-secondary-600 mb-3">
                  Som partner kan du gi intime, romantiske eller dypt personlige gaver. Du kjenner hennes 
                  drømmer, ønsker og små hverdagsgleder best av alle.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-2">Perfekt for dere:</h4>
                    <ul className="text-secondary-600 space-y-1">
                      <li>• Romantiske opplevelser for to</li>
                      <li>• Personaliserte smykker eller kunst</li>
                      <li>• Intimt undertøy eller parfyme</li>
                      <li>• Felles hobbyer og interesser</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-2">Unngå:</h4>
                    <ul className="text-secondary-600 space-y-1">
                      <li>• Praktiske hverdagsgjenstander</li>
                      <li>• Gaver som kritiserer (treningsutstyr)</li>
                      <li>• Alt som føles upersonlig</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-accent-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">👨‍👩‍👧‍👦 Familie</h3>
                <p className="text-secondary-600 mb-3">
                  Som familiemedlem kan du gi nostalgiske gaver, støtte hennes rolle eller 
                  anerkjenne hennes betydning i familiens liv.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-2">Perfekt for familie:</h4>
                    <ul className="text-secondary-600 space-y-1">
                      <li>• Familiefoto i vakker ramme</li>
                      <li>• Støtte hennes morsrolle</li>
                      <li>• Barndomsminner og nostalgi</li>
                      <li>• Tid til avslapning og selvpleie</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-secondary-700 mb-2">Spesielt relevant:</h4>
                    <ul className="text-secondary-600 space-y-1">
                      <li>• Mor: Anerkjenn hennes omsorg</li>
                      <li>• Søster: Feir deres unike bånd</li>
                      <li>• Datter: Vis stolthet og støtte</li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-success-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">👭 Venninne</h3>
                <p className="text-secondary-600 mb-3">
                  Som venninne fokuser på moro, delte opplevelser og det som støtter hennes interesser 
                  og drømmer. Vennskap handler om å feire hverandre.
                </p>
                <div className="text-sm">
                  <h4 className="font-semibold text-secondary-700 mb-2">Populære venninne-gaver:</h4>
                  <ul className="text-secondary-600 space-y-1">
                    <li>• Spa-dag eller wellness-opplevelse sammen</li>
                    <li>• Konsertbilletter eller kulturelle opplevelser</li>
                    <li>• Skjønnhetsprodukter og pamper-pakker</li>
                    <li>• Bøker, vin eller koselige kvelder hjemme</li>
                    <li>• Noe knyttet til hennes spesielle interesser</li>
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border-l-4 border-purple-500">
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">🏢 Kollega/Bekjent</h3>
                <p className="text-secondary-600 mb-3">
                  Hold deg til profesjonelle, universelle gaver som viser respekt uten å bli for personlige. 
                  Kvalitet og smak er viktigere enn personlig tilkobling.
                </p>
                <div className="text-sm">
                  <h4 className="font-semibold text-secondary-700 mb-2">Trygge valg:</h4>
                  <ul className="text-secondary-600 space-y-1">
                    <li>• Gavekort til butikker eller opplevelser</li>
                    <li>• Kvalitetskaffe, te eller gourmet-produkter</li>
                    <li>• Bøker eller tidsskrift-abonnement</li>
                    <li>• Planter eller blomster</li>
                    <li>• Kontortilbehør i god kvalitet</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Unngå disse gavefeilene
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-error-50 border border-error-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-error-800 mb-4">❌ Ikke gjør dette</h3>
                <ul className="space-y-2 text-error-700">
                  <li>• Gi klær uten å vite størrelse og stil</li>
                  <li>• Anta hun vil like rosa bare fordi hun er kvinne</li>
                  <li>• Gi praktiske gaver når anledningen krever romantikk</li>
                  <li>• Kjøpe billige imitasjoner av designervarer</li>
                  <li>• Gi noe som kritiserer (treningsutstyr uten at hun har bedt om det)</li>
                  <li>• Glemme å inkludere en gjennomtenkt hilsen</li>
                  <li>• Gi gavekort som virker lat eller upersonlig</li>
                </ul>
              </div>

              <div className="bg-success-50 border border-success-200 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-success-800 mb-4">✅ Gjør heller dette</h3>
                <ul className="space-y-2 text-success-700">
                  <li>• Observer hva hun bruker tid på og liker</li>
                  <li>• Spør diskret om ønsker eller interesser</li>
                  <li>• Velg kvalitet fremfor kvantitet</li>
                  <li>• Tenk på hennes personlige stil og preferanser</li>
                  <li>• Gi opplevelser hvis du er usikker på fysiske ting</li>
                  <li>• Skriv en hjertelig og personlig hilsen</li>
                  <li>• Pakk gaven vakkert - presentasjonen betyr mye</li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-primary-50 border border-primary-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Sparkles className="w-6 h-6 text-primary-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-primary-800 mb-2">Hemmeligheten bak den perfekte gaven</h3>
                  <p className="text-primary-700">
                    Den beste gaven til henne er en som viser at du har lagt merke til hvem hun er som person - 
                    hennes drømmer, interesser, utfordringer og ønsker. Det handler ikke om pris, men om 
                    at du har tenkt på henne som det unike individet hun er.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary-500 to-purple-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å finne gaven som får henne til å stråle?
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              Bruk vår gavefinner for å få personlige anbefalinger basert på hennes unike stil og interesser
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-primary-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('recipient')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-primary-600 transition-colors font-semibold"
              >
                Se andre mottakere
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ForHerPage;